if (!customElements.get('quick-buy')) {
  const quickBuyFragments = new Map();
  class QuickBuy extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.trigger = this.querySelector('[data-quick-buy-trigger]');
      this.loadStatus = this.querySelector('[data-quick-buy-load-status]');
      if (!this.trigger) return;
      this.initialized = true;
      this.trigger.addEventListener('click', () => this.openDialog());
      const prefetch = () => this.getDialogMarkup().catch(() => {});
      this.trigger.addEventListener('pointerenter', prefetch, { once: true });
      this.trigger.addEventListener('focus', prefetch, { once: true });
      this.bindDialog();
    }
    getDialogMarkup() {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      const localeRoot = window.Shopify?.routes?.root || '/';
      if (!url.pathname.startsWith(localeRoot)) {
        url.pathname = localeRoot.replace(/\/$/, '') + url.pathname;
      }
      if (url.origin !== window.location.origin) return Promise.reject(new Error(this.dataset.errorText));
      url.searchParams.set('section_id', 'quick-buy-modal');
      if (!quickBuyFragments.has(url.href)) {
        const request = fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
          .then((response) => {
            if (!response.ok) throw new Error(this.dataset.errorText);
            return response.text();
          })
          .then((html) => {
            const dialog = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-buy-dialog]');
            if (!dialog) throw new Error(this.dataset.errorText);
            return dialog;
          });
        quickBuyFragments.set(url.href, request);
        request.catch(() => quickBuyFragments.delete(url.href));
      }
      return quickBuyFragments.get(url.href);
    }
    async ensureDialog() {
      if (this.dialog) return;
      const source = await this.getDialogMarkup();
      const dialog = document.importNode(source, true);
      const prefix = this.dataset.quickBuyId;
      [dialog, ...dialog.querySelectorAll('[id], [for], [aria-labelledby], [aria-describedby]')].forEach((element) => {
        ['id', 'for', 'aria-labelledby', 'aria-describedby'].forEach((attribute) => {
          const value = element.getAttribute(attribute);
          if (value) element.setAttribute(attribute, value.replaceAll('QuickBuy-Lazy', prefix));
        });
      });
      this.append(dialog);
      if (!this.bindDialog()) throw new Error(this.dataset.errorText);
    }
    bindDialog() {
      if (this.dialogBound) return true;
      this.dialog = this.querySelector('[data-quick-buy-dialog]');
      if (!this.dialog) return false;
      this.closeButton = this.querySelector('[data-quick-buy-close]');
      this.form = this.querySelector('[data-quick-buy-form]');
      this.buttons = Array.from(this.querySelectorAll('[type="submit"]'));
      this.status = this.querySelector('.quick-buy__status');
      this.price = this.querySelector('.quick-buy__price');
      this.image = this.querySelector('.quick-buy__image');
      this.defaultImage = this.image?.getAttribute('src');
      this.optionSelects = Array.from(this.querySelectorAll('[data-quick-buy-option]'));
      this.variantSelect = this.querySelector('[data-quick-buy-variant]');
      this.quantityInput = this.querySelector('[data-quick-buy-quantity-stepper] input[name="quantity"]');
      this.quantityDecrease = this.querySelector('[data-quick-buy-quantity-decrease]');
      this.quantityIncrease = this.querySelector('[data-quick-buy-quantity-increase]');
      if (!this.closeButton || !this.form) return false;
      this.dialogBound = true;
      this.closeButton.addEventListener('click', () => this.closeDialog());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      });
      this.dialog.addEventListener('close', () => {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (this.dialog.contains(document.activeElement)) this.trigger.focus();
      });
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.quantityDecrease?.addEventListener('click', () => this.changeQuantity(-1));
      this.quantityIncrease?.addEventListener('click', () => this.changeQuantity(1));
      this.quantityInput?.addEventListener('input', () => this.normaliseQuantity());
      this.optionSelects.forEach((select) => {
        select.addEventListener('change', () => this.syncVariant());
      });
      return true;
    }
    async openDialog() {
      if (this.loading) return;
      this.loading = true;
      this.trigger.disabled = true;
      this.trigger.setAttribute('aria-busy', 'true');
      if (this.loadStatus) this.loadStatus.textContent = '';
      try {
        await this.ensureDialog();
        this.buttons.forEach((button) => { button.textContent = button.dataset.defaultText; });
        if (this.quantityInput) this.quantityInput.value = '1';
        this.syncVariant();
        if (!this.dialog.open) {
          if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
          else this.dialog.setAttribute('open', '');
        }
        this.trigger.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (this.loadStatus) this.loadStatus.textContent = this.dataset.errorText;
      } finally {
        this.loading = false;
        this.trigger.disabled = false;
        this.trigger.removeAttribute('aria-busy');
      }
    }
    changeQuantity(delta) {
      if (!this.quantityInput) return;
      const current = Math.max(1, parseInt(this.quantityInput.value, 10) || 1);
      this.quantityInput.value = String(Math.max(1, current + delta));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    normaliseQuantity() {
      if (!this.quantityInput) return;
      const current = parseInt(this.quantityInput.value, 10);
      this.quantityInput.value = String(Number.isFinite(current) && current >= 1 ? current : 1);
    }
    closeDialog() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.dialog.contains(document.activeElement)) this.trigger.focus();
    }
    syncVariant() {
      const variantSelect = this.variantSelect;
      if (!variantSelect) return;
      const selectedOptions = this.optionSelects.map((select) => select.value);
      const matching = Array.from(variantSelect.options).find((option) => {
        const options = JSON.parse(option.dataset.options || '[]');
        return options.length === selectedOptions.length && options.every((value, index) => value === selectedOptions[index]);
      });
      if (matching && !matching.disabled) {
        variantSelect.value = matching.value;
        variantSelect.removeAttribute('aria-invalid');
      } else {
        variantSelect.value = '';
        variantSelect.setAttribute('aria-invalid', 'true');
      }
      if (matching) {
        this.price.textContent = matching.dataset.price;
        const imageSource = matching.dataset.image || this.defaultImage;
        if (this.image && imageSource && this.image.getAttribute('src') !== imageSource) {
          this.image.removeAttribute('srcset');
          this.image.src = imageSource;
        }
      } else {
        this.price.textContent = this.dataset.unavailableText;
      }
      this.variantAvailable = Boolean(matching && !matching.disabled);
      this.status.textContent = this.variantAvailable ? '' : (matching ? this.dataset.soldOutText : this.dataset.unavailableText);
      this.buttons.forEach((button) => { button.disabled = this.pending || !this.variantAvailable; });
    }
    async submit(event) {
      event.preventDefault();
      this.syncVariant();
      const submitButton = event.submitter || this.buttons[0];
      const buyNow = submitButton.dataset.buyNow === 'true';
      const variantSelect = this.querySelector('[data-quick-buy-variant]');
      if (variantSelect && !variantSelect.value) {
        this.status.textContent = this.dataset.unavailableText;
        return;
      }
      if (submitButton.disabled) return;
      const cartNotification = document.querySelector('cart-notification');
      const config = window.theme && typeof window.theme.fetchConfig === 'function' ? window.theme.fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/json' } };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(this.form);
      if (cartNotification) {
        cartNotification.setActiveElement(this.trigger);
        formData.append('sections', cartNotification.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
      }
      this.pending = true;
      this.optionSelects.forEach((select) => { select.disabled = true; });
      this.buttons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });
      const requestFormData = new FormData();
      for (const [name, value] of formData.entries()) {
        const property = name.match(/^properties\[(.+)\]$/);
        const requestName = ['id', 'quantity', 'selling_plan', 'parent_id'].includes(name)
          ? `items[0][${name}]`
          : property ? `items[0][properties][${property[1]}]` : name;
        requestFormData.append(requestName, value);
      }
      config.body = requestFormData;
      try {
        const response = await fetch(this.form.action, config);
        const data = await response.json().catch(() => ({}));
        const addedItem = data.items?.[0] || data;
        if (!response.ok || data.status || !addedItem.id) throw new Error(data.description || this.dataset.errorText);
        if (buyNow) {
          window.location.assign('/checkout');
          return;
        }
        this.closeDialog();
        if (cartNotification && data.sections) {
          cartNotification.renderContents({ ...addedItem, sections: data.sections }, { quantity: formData.get('quantity') });
        } else {
          submitButton.textContent = submitButton.dataset.addedText;
          this.status.textContent = submitButton.dataset.addedText;
        }
      } catch (error) {
        this.status.textContent = error.message;
      } finally {
        this.pending = false;
        this.optionSelects.forEach((select) => { select.disabled = false; });
        this.buttons.forEach((button) => {
          button.disabled = Boolean(this.variantSelect && !this.variantSelect.value);
          button.removeAttribute('aria-busy');
        });
      }
    }
  }
  customElements.define('quick-buy', QuickBuy);
}


